// Deep-link sheet-nyitó hook — a sheet-alapú lista-oldalak beúszó sheetjét egy URL-search-paraméterből
// (`?edit=<id>` / `?open=<id>`) nyitja meg a megadott azonosítójú entitásra. A command palette (és bármely
// megosztott link) így egy konkrét entitásra tud ugorva megnyitni egy sheetet — deep-linkelhető modal
// (megosztható + back-gomb-barát).
//
// Működés: amikor a param jelen van ÉS a hozzá tartozó sor MÁR betöltődött a listába, egyszer meghívja az
// `onOpen`-t a megtalált sorral (a lokális sheet-állapot beállítása a hívó dolga). Ha a sor még nincs a betöltött
// lapon (async lista) vagy nincs hozzá jogosultság → NEM hív (a sheet nem nyílik, az oldal normálisan renderelődik),
// és a lista frissülésekor újrapróbálja. A param törléséig (a sheet záráskor a hívó törli, `navigate` replace-szel)
// egy adott id-ra csak EGYSZER nyit; a param eltűnésekor a jelölő resetel → ugyanaz az id ÚJRA deep-linkelhető.
import { useEffect, useRef } from 'react';

export interface UseDeepLinkSheetOptions<T> {
  /** A deep-link search-paraméter aktuális értéke (`?edit`/`?open`); `undefined` → nincs deep-link. */
  id: string | undefined;
  /** A betöltött lista-sorok, amikből a cél-entitást keressük. */
  rows: readonly T[];
  /** Egy sor azonosítója (a paraméterrel összevetve). */
  getId: (row: T) => string;
  /** A megtalált sorral megnyitja a sheetet (a lokális állapot beállítása). Egy id-ra egyszer hívódik. */
  onOpen: (row: T) => void;
}

export function useDeepLinkSheet<T>({ id, rows, getId, onOpen }: UseDeepLinkSheetOptions<T>): void {
  // Az utoljára KEZELT (megnyitott) id — hogy ne nyissuk újra ugyanarra, amíg a param jelen van. A param
  // eltűnésekor null-ra reset → a záráskori param-törlés után ugyanaz az id megint deep-linkelhető.
  const handledId = useRef<string | undefined>(undefined);
  // A legfrissebb callbackek ref-ben — így az effekt függősége csak [id, rows] (nincs újrafutás minden rendernél).
  const getIdRef = useRef(getId);
  const onOpenRef = useRef(onOpen);
  getIdRef.current = getId;
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (!id) {
      handledId.current = undefined;
      return;
    }
    if (handledId.current === id) return;
    const row = rows.find((r) => getIdRef.current(r) === id);
    // Még nincs a betöltött lapon (vagy nincs jog) → nem nyitunk; a rows frissülésekor újrapróbálunk.
    if (!row) return;
    handledId.current = id;
    onOpenRef.current(row);
  }, [id, rows]);
}
